import { fetchInfo, createOrder } from "@/api/modules/products/zonetickets";
import {
    LOCATION_FROM,
    LOCATION_VIA,
    LOCATION_TO
} from "@/store/modules/location";
import { makeUtcDate } from "@/utilities/time";

const InfoModel = function(info) {
    this.identifier = info.productTypeIdentifier;
    // Javascript will interpret the result as text
    this.allowedPassengerTypeIds = info.allowedPassengerTypeIds.map(id =>
        parseInt(id)
    );
    this.maximumNumberOfPassengersPerItem =
        info.maximumNumberOfPassengersPerItem;
    this.maximumStartValidityDaysFromNow = info.maximumStartValidityDaysFromNow;
    this.minZoneDistance = info.minZoneDistance;
    this.maxZoneDistance = info.maxZoneDistance;

    this.startStopAndEndStopMode = new StartStopAndEndStopModeModel(
        info.startZoneAndEndZoneMode
    );
    this.startStopAndZoneDistanceMode = new StartStopAndZoneDistanceMode(
        info.startZoneAndZoneDistanceMode
    );
};

const StartStopAndEndStopModeModel = function(mode) {
    this.available = mode.available;
    this.tripSelectionRequired = mode.tripSelectionRequired;
    this.viaStopAvailable = mode.viaStopAvailable;
};

const StartStopAndZoneDistanceMode = function(mode) {
    this.available = mode.available;
};

export const state = {
    info: null
};

export const mutations = {
    updateInfo: (state, payload) => {
        state.info = new InfoModel(payload);
    }
};

export const getters = {
    isEnabled: state => Boolean(state.info)
};

export const actions = {
    fetchInfo: context => {
        return fetchInfo(true)
            .then(response => {
                if (!response.ok) throw new Error(response.responseBody);
                return response.responseBody;
            })
            .then(json => {
                context.commit("updateInfo", json);
                context.commit(
                    "system/addStopProductType",
                    {
                        productType: context.state.info.identifier
                    },
                    { root: true }
                );
            });
    },
    prepareOrder: context => {
        const {
            allowedPassengerTypeIds,
            maximumNumberOfPassengersPerItem,
            maximumStartValidityDaysFromNow
        } = context.state.info;
        const serverTime = makeUtcDate(
            context.rootGetters.getServerDateAsMoment
        );
        const periodStart = makeUtcDate(serverTime)
            .add(-1, "days")
            .endOf("day");
        const periodEnd = makeUtcDate(serverTime)
            .add(maximumStartValidityDaysFromNow, "days")
            .startOf("day")
            .add(-1, "second");

        context.commit(
            "passenger/updatePassengers",
            {
                passengerTypeIds: allowedPassengerTypeIds,
                maximumPassengers: maximumNumberOfPassengersPerItem
            },
            { root: true }
        );
        context.dispatch(
            "date/prepareSelectableDates",
            { periodStart, periodEnd },
            { root: true }
        );
    },
    createOrder: context => {
        const passengers = context.rootGetters["passenger/getPassengers"].map(
            passenger => {
                return {
                    passengerTypeId: passenger.passengerType.id,
                    passengerTypeCount: passenger.count
                };
            }
        );

        const validFromInstance =
            context.rootGetters["date/getDepartureDateAsMoment"];
        const startLocation = context.rootGetters["location/getLocationByType"](
            LOCATION_FROM
        );
        const viaLocation = context.rootGetters["location/getLocationByType"](
            LOCATION_VIA
        );
        const endLocation = context.rootGetters["location/getLocationByType"](
            LOCATION_TO
        );

        let viaStopId = null;
        let viaZoneNumber = null;

        if (viaLocation != null) {
            viaStopId = viaLocation.id;
            viaZoneNumber = viaLocation.zoneNumber;
        }

        // TODO This should be changed so it supports searching for a zone without a stop, when Midttrafik support is needed
        return createOrder(
            passengers,
            startLocation.zoneNumber,
            viaZoneNumber,
            endLocation.zoneNumber,
            startLocation.id,
            viaStopId,
            endLocation.id,
            validFromInstance,
            true
        )
            .then(response => {
                if (response.ok) {
                    return response.responseBody;
                } else if (
                    response.status === 422 &&
                    response.responseBody.error === "ZoneDistanceNoTripFound"
                ) {
                    throw new Error("ZoneDistanceNoTripFound");
                } else if (
                    response.status === 422 &&
                    response.responseBody.error === "StopIdentityConflict"
                ) {
                    throw new Error("StopIdentityConflict");
                } else if (
                    response.status === 503 &&
                    response.responseBody.errorType === "ServiceUnavailable"
                ) {
                    throw new Error("ServiceUnavailable");
                } else {
                    throw new Error(JSON.stringify(response));
                }
            })
            .then(({ orderId, priceOre, ...details }) => {
                context.commit("order/setOrderId", orderId, { root: true });
                context.commit("order/setPriceOre", priceOre, { root: true });
                context.commit(
                    "order/setDetails",
                    {
                        ...details,
                        validFrom: makeUtcDate(details.validFromInstant),
                        validTo: makeUtcDate(details.validToInstant)
                    },
                    { root: true }
                );
            });
    }
};
